import React, { useEffect, useRef, useState } from 'react';
import { owner, files as filesApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Plus, Edit3, Trash2, X, Loader2, Ticket, Camera, Calendar, Lock } from 'lucide-react';
import ClampWithMore from '../components/ClampWithMore';
import TierGate from '../components/TierGate';
import AppImage from '../components/AppImage';

const BLANK = { name:'', description:'', discount:'', expiration:'', terms:'', redemption_instructions:'', image:'' };

export default function MyCoupons() {
  return (
    <TierGate feature="coupons">
      <MyCouponsInner />
    </TierGate>
  );
}

function MyCouponsInner() {
  const [myBiz, setMyBiz] = useState([]);
  const [selectedBiz, setSelectedBiz] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [gateMsg, setGateMsg] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    owner.myBusinesses().then(bs => {
      setMyBiz(bs);
      if (bs.length) setSelectedBiz(bs[0].id);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedBiz) return;
    setLoadingList(true); setGateMsg('');
    owner.listCoupons(selectedBiz).then(setItems).catch(err => {
      setItems([]);
      if (err?.response?.status === 402) setGateMsg(err.response.data?.detail || 'Package required to manage coupons.');
    }).finally(() => setLoadingList(false));
  }, [selectedBiz]);

  const openNew = () => { setForm(BLANK); setEditing('new'); };
  const openEdit = (c) => { setForm({ ...BLANK, ...c }); setEditing(c); };
  const close = () => setEditing(null);
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const res = await filesApi.upload(f, 'business');
      setF('image', res.url);
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setUploading(false); e.target.value=''; }
  };

  const save = async () => {
    if (!form.name) { toast({ title: 'Coupon name is required' }); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        const created = await owner.addCoupon(selectedBiz, form);
        setItems(prev => [created, ...prev]);
      } else {
        const updated = await owner.updateCoupon(selectedBiz, editing.id, form);
        setItems(prev => prev.map(c => c.id === editing.id ? updated : c));
      }
      close();
      toast({ title: editing === 'new' ? 'Coupon created!' : 'Coupon updated' });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    await owner.deleteCoupon(selectedBiz, id);
    setItems(prev => prev.filter(c => c.id !== id));
  };

  if (loading) return <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading...</div>;

  if (myBiz.length === 0) return (
    <div className="pbb-card p-10 text-center max-w-xl mx-auto">
      <Ticket className="mx-auto text-green-400" size={28}/>
      <div className="mt-3 font-semibold">Create a listing first</div>
      <p className="text-sm text-gray-400 mt-1">You need at least one business listing before creating coupons.</p>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">My Coupons</h1>
          <p className="text-gray-400 text-sm">Create promotions your customers redeem in the app.</p>
        </div>
        <button onClick={openNew} disabled={!!gateMsg} className="px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm inline-flex items-center gap-2 disabled:opacity-50">
          <Plus size={16}/> Add Coupon
        </button>
      </div>

      <div className="pbb-card p-4 flex items-center gap-3 flex-wrap">
        <div className="text-sm text-gray-400">For business:</div>
        <select className="pbb-input" style={{ maxWidth: 320 }} value={selectedBiz} onChange={e => setSelectedBiz(e.target.value)}>
          {myBiz.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {gateMsg ? (
        <div className="pbb-card p-8 text-center">
          <Lock className="mx-auto text-green-400" size={26}/>
          <div className="mt-3 font-semibold">Package Required</div>
          <p className="text-sm text-gray-400 mt-1">{gateMsg}</p>
          <a href="/business/upgrade" className="pbb-btn-green mt-5 inline-block max-w-xs">View packages</a>
        </div>
      ) : loadingList ? (
        <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading coupons...</div>
      ) : items.length === 0 ? (
        <div className="pbb-card p-10 text-center">
          <Ticket className="mx-auto text-green-400" size={26}/>
          <div className="mt-3 font-semibold">No coupons yet</div>
          <p className="text-sm text-gray-400 mt-1">Add your first coupon so members can redeem it.</p>
          <button onClick={openNew} className="pbb-btn-green mt-5 max-w-xs mx-auto">Add your first coupon</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(c => (
            <div key={c.id} className="pbb-card overflow-hidden flex flex-col h-full">
              <div className="h-1.5 bg-green-500" />
              <AppImage src={filesApi.urlFor(c.image)} alt={c.name || 'Coupon'} rounded="none" fallback={<Ticket size={24}/>} />
              <div className="p-4 flex flex-col flex-1">
                <div className="flex items-center gap-2 text-green-400"><Ticket size={16}/> <span className="font-semibold text-sm">{c.name}</span></div>
                {c.discount && <div className="text-lg font-bold text-white mt-1">{c.discount}</div>}
                {c.description && <ClampWithMore text={c.description} lines={3} className="text-xs text-gray-400 mt-1" testId={`my-coupon-desc-${c.id}`} />}
                {c.expiration && <div className="text-xs text-gray-500 mt-2 inline-flex items-center gap-1"><Calendar size={11}/> Expires {c.expiration}</div>}
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <button onClick={() => openEdit(c)} className="px-3 py-1.5 rounded-lg border border-[#2a2a2a] hover:border-green-600 text-xs inline-flex items-center justify-center gap-1"><Edit3 size={12}/> Edit</button>
                  <button onClick={() => remove(c.id)} className="px-3 py-1.5 rounded-lg border border-[#2a2a2a] hover:border-red-500 hover:text-red-400 text-xs inline-flex items-center justify-center gap-1"><Trash2 size={12}/> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-4" onClick={close}>
          <div className="pbb-card w-full max-w-lg p-5 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold text-lg">{editing === 'new' ? 'New Coupon' : 'Edit Coupon'}</div>
              <button onClick={close} className="text-gray-400"><X size={18}/></button>
            </div>
            <div className="space-y-3">
              <input className="pbb-input" placeholder="Title *  (e.g. 20% off entrees)" value={form.name} onChange={e => setF('name', e.target.value)}/>
              <textarea rows={2} className="pbb-input" placeholder="Description  (what customers get)" value={form.description} onChange={e => setF('description', e.target.value)}/>
              <input className="pbb-input" placeholder="Discount amount  (e.g. 20%, $10, BOGO)" value={form.discount} onChange={e => setF('discount', e.target.value)}/>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Expiry date</label>
                <input type="date" className="pbb-input" value={form.expiration || ''} onChange={e => setF('expiration', e.target.value)}/>
              </div>
              <textarea rows={2} className="pbb-input" placeholder="Terms & conditions" value={form.terms} onChange={e => setF('terms', e.target.value)}/>
              <textarea rows={2} className="pbb-input" placeholder="Redemption instructions  (how customer redeems)" value={form.redemption_instructions} onChange={e => setF('redemption_instructions', e.target.value)}/>
              <div className="flex items-center gap-3">
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-[#0d1f14] flex items-center justify-center relative">
                  {form.image ? <img src={filesApi.urlFor(form.image)} alt="" className="w-full h-full object-cover"/> : <Ticket className="text-gray-600" size={22}/>}
                  {uploading && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 size={14} className="animate-spin text-white"/></div>}
                </div>
                <div>
                  <button onClick={() => fileRef.current?.click()} disabled={uploading} className="px-3 py-2 rounded-lg border border-[#2a2a2a] hover:border-green-600 text-xs inline-flex items-center gap-1">
                    <Camera size={12}/> {form.image ? 'Change image' : 'Add image (optional)'}
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile}/>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={close} className="flex-1 px-3 py-2.5 rounded-lg border border-[#2a2a2a] text-sm">Cancel</button>
              <button onClick={save} disabled={saving} className="flex-1 pbb-btn-green disabled:opacity-70 inline-flex items-center justify-center gap-2">
                {saving && <Loader2 className="animate-spin" size={14}/>} {editing === 'new' ? 'Create' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
